/**
 * OnboardingContext
 *
 * Manages guided onboarding state across pages.
 * Tracks current step and provides methods to advance/skip onboarding.
 */

import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';

/**
 * Onboarding phases - each page handles its own steps within a phase
 */
export type OnboardingPhase = 'gyms' | 'climb' | 'profile' | 'complete';

/**
 * Context value interface
 */
export interface OnboardingContextValue {
  /** Whether onboarding is currently active */
  isOnboarding: boolean;
  /** Current phase of onboarding */
  currentPhase: OnboardingPhase;
  /** Current step within the phase (0-indexed) */
  currentStep: number;
  /** Advance to the next step */
  nextStep: () => void;
  /** Move to the next phase */
  nextPhase: () => void;
  /** Skip/complete onboarding entirely */
  completeOnboarding: () => Promise<void>;
  /** Start onboarding (for testing/manual trigger) */
  startOnboarding: () => void;
}

const defaultContextValue: OnboardingContextValue = {
  isOnboarding: false,
  currentPhase: 'gyms',
  currentStep: 0,
  nextStep: () => {},
  nextPhase: () => {},
  completeOnboarding: async () => {},
  startOnboarding: () => {},
};

const OnboardingContext = createContext<OnboardingContextValue>(defaultContextValue);

export interface OnboardingProviderProps {
  children: ReactNode;
}

/**
 * Onboarding Provider
 *
 * Wraps the app to provide onboarding state management.
 * Automatically activates for users who haven't completed onboarding.
 */
export const OnboardingProvider = ({ children }: OnboardingProviderProps) => {
  const { user, userProfile, loading, markAsOnboarded } = useAuth();

  // Derive initial onboarding state from user profile
  // shouldOnboard is true when:
  // - User is logged in (user exists)
  // - Auth loading is complete
  // - Either userProfile is null (new user, profile not created yet)
  //   OR userProfile.isOnboarded is explicitly false
  const shouldOnboard = !loading && !!user && (userProfile === null || !userProfile.isOnboarded);

  const [isOnboarding, setIsOnboarding] = useState(false);
  const [currentPhase, setCurrentPhase] = useState<OnboardingPhase>('gyms');
  const [currentStep, setCurrentStep] = useState(0);

  // Start onboarding when user profile loads and they haven't onboarded
  // This is checked by individual pages that participate in onboarding
  const startOnboarding = useCallback(() => {
    if (shouldOnboard) {
      setIsOnboarding(true);
      setCurrentPhase('gyms');
      setCurrentStep(0);
    }
  }, [shouldOnboard]);

  // Advance to next step within current phase
  const nextStep = useCallback(() => {
    setCurrentStep((prev) => prev + 1);
  }, []);

  // Move to next phase (resets step to 0)
  const nextPhase = useCallback(() => {
    setCurrentStep(0);
    setCurrentPhase((prev) => {
      switch (prev) {
        case 'gyms':
          return 'climb';
        case 'climb':
          return 'profile';
        case 'profile':
          return 'complete';
        default:
          return 'complete';
      }
    });
  }, []);

  // Complete onboarding - mark user as onboarded in Firestore
  const completeOnboarding = useCallback(async () => {
    setIsOnboarding(false);
    setCurrentPhase('complete');
    setCurrentStep(0);

    try {
      await markAsOnboarded();
    } catch (err) {
      console.error('Failed to mark user as onboarded:', err);
    }
  }, [markAsOnboarded]);

  const value = useMemo<OnboardingContextValue>(
    () => ({
      isOnboarding,
      currentPhase,
      currentStep,
      nextStep,
      nextPhase,
      completeOnboarding,
      startOnboarding,
    }),
    [
      isOnboarding,
      currentPhase,
      currentStep,
      nextStep,
      nextPhase,
      completeOnboarding,
      startOnboarding,
    ]
  );

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
};

/**
 * Hook to access onboarding state and methods
 */
export const useOnboarding = (): OnboardingContextValue => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

export default OnboardingContext;
